// 자유 스탯 포인트(레인 C, docs/PC-WINDOWS-PRD.md 3절). 레벨업마다 스탯 포인트를 받아 힘·민첩·지능·체력에 나눈다.
// 수치 공식의 정본은 이 파일 하나(레인 B 밸런스가 검토·조정한다). 순수 함수·할당 없음(out 재사용) — 프레임 루프에서 부르지 않고 로드·레벨업·배분 때만 부른다.
import type { GrowthMods } from './growth';
import { LEVEL_MAX } from './growth';

export type StatId = 'str' | 'dex' | 'int' | 'vit';
export const STAT_IDS: readonly StatId[] = ['str', 'dex', 'int', 'vit'];

/** 배분(포인트 수). 저장 형식 dungeonhero.stats.v1의 한 칸 */
export interface StatAlloc { readonly str: number; readonly dex: number; readonly int: number; readonly vit: number }
export const EMPTY_STATS: StatAlloc = Object.freeze({ str: 0, dex: 0, int: 0, vit: 0 });

/** 레벨업 1회당 받는 포인트. Lv1 = 0점, Lv99 = 196점 */
export const STAT_POINTS_PER_LEVEL = 2;
/** 표시용 기본 능력치(모든 영웅 같다 — 배분 포인트만 효과가 있다) */
export const STAT_BASE = 10;

/**
 * 포인트 1점의 효과(2026-10-28, 레인 B 검토 반영 — docs/BALANCE-1028.md). 레벨 기본 성장(LEVEL_STATS 공격 +1·HP +8/레벨)의 절반 안쪽으로 잡았다:
 * 힘에 전부(196점) 넣어도 공격 +58, 체력에 전부 넣어도 HP +784.
 * 민첩 치명은 상한이 있다(장비·트리 치명과 합쳐 너무 오르지 않게).
 */
export const STAT_EFFECT = {
  /** 힘: 공격력 +0.3/점(최종 공격력에서 반올림) — 레인 B 2026-10-28: 0.5면 힘 몰빵 시 지역1·2 보스가 목표 하한 30초를 깬다 */
  strAttack: 0.3,
  /** 민첩: 치명 확률 +0.1%p/점, 상한 +10%p */
  dexCrit: 0.001,
  dexCritCap: 0.1,
  /** 지능: 최대 MP +1/점, MP 재생 +0.2%/점 — 레인 B 2026-10-28: MP 절제감·유니크 가치 유지(tools/sim/mp.ts) */
  intMp: 1,
  intRegen: 0.002,
  /** 체력: 최대 HP +4/점 */
  vitHp: 4,
} as const;

/** 그 레벨까지 받은 스탯 포인트 */
export function statPointsAt(level: number): number {
  const lv = Math.max(1, Math.min(LEVEL_MAX, Math.floor(level)));
  return (lv - 1) * STAT_POINTS_PER_LEVEL;
}

export function statSum(a: StatAlloc): number { return a.str + a.dex + a.int + a.vit; }

const okInt = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
/** 배분이 형식상 맞고 포인트 안인가 */
export function statAllocValid(a: StatAlloc, points: number): boolean {
  return okInt(a.str) && okInt(a.dex) && okInt(a.int) && okInt(a.vit) && statSum(a) <= points;
}

/**
 * 저장된 배분이 지금 포인트보다 많을 때(환생으로 레벨 1, 다른 탭의 낮은 레벨 기록 등) 효과에 쓸 배분.
 * 넘치면 비례로 줄인다(내림). 저장은 바꾸지 않는다 — 레벨이 다시 오르면 원래 배분이 되살아난다
 */
export function fitStats(out: { str: number; dex: number; int: number; vit: number }, a: StatAlloc, points: number): StatAlloc {
  const sum = statSum(a);
  if (sum <= points) { out.str = a.str; out.dex = a.dex; out.int = a.int; out.vit = a.vit; return out; }
  const k = points > 0 ? points / sum : 0;
  out.str = Math.floor(a.str * k); out.dex = Math.floor(a.dex * k); out.int = Math.floor(a.int * k); out.vit = Math.floor(a.vit * k);
  return out;
}

/** 배분 효과를 성장 보정(growthMods 결과)에 더한다. growthMods가 매번 값을 새로 쓰므로 그 직후에 한 번만 부른다 */
export function applyStats(out: GrowthMods, a: StatAlloc): GrowthMods {
  // 힘 공격은 내림하지 않는다: 소수 합을 deriveStats가 최종 공격력에서 반올림한다(0.3 × 3점 = +0.9 → 공격 +1, 1~2점도 쌓인다)
  out.attackAdd += a.str * STAT_EFFECT.strAttack;
  out.critAdd += Math.min(STAT_EFFECT.dexCritCap, a.dex * STAT_EFFECT.dexCrit);
  out.maxMpAdd += Math.floor(a.int * STAT_EFFECT.intMp);
  out.mpRegenMult += a.int * STAT_EFFECT.intRegen;
  out.maxHpAdd += a.vit * STAT_EFFECT.vitHp;
  return out;
}

/** 창에 보일 한 줄(이름·효과 설명·지금 효과) */
export interface StatRow { readonly id: StatId; readonly name: string; readonly short: string; readonly per: string; readonly value: number; readonly effect: string }
export const STAT_NAME: Readonly<Record<StatId, string>> = { str: '힘', dex: '민첩', int: '지능', vit: '체력' };
export const STAT_SHORT: Readonly<Record<StatId, string>> = { str: 'STR', dex: 'DEX', int: 'INT', vit: 'VIT' };
export const STAT_PER: Readonly<Record<StatId, string>> = {
  str: '1점당 공격력 +0.3',
  dex: '1점당 치명 확률 +0.1% (최대 +10%)',
  int: '1점당 최대 MP +1 · MP 재생 +0.2%',
  vit: '1점당 최대 HP +4',
};

/** 지금 배분의 효과 문구(창 표시용 — 순수, 창을 열 때만 부른다) */
export function statEffectText(id: StatId, a: StatAlloc): string {
  switch (id) {
    case 'str': return '공격력 +' + (Math.round(a.str * STAT_EFFECT.strAttack * 10) / 10);
    case 'dex': return '치명 +' + (Math.min(STAT_EFFECT.dexCritCap, a.dex * STAT_EFFECT.dexCrit) * 100).toFixed(1) + '%';
    case 'int': return '최대 MP +' + Math.floor(a.int * STAT_EFFECT.intMp) + ' · 재생 +' + Math.round(a.int * STAT_EFFECT.intRegen * 100) + '%';
    case 'vit': return '최대 HP +' + a.vit * STAT_EFFECT.vitHp;
  }
}

export function statRows(a: StatAlloc): StatRow[] {
  return STAT_IDS.map((id) => ({ id, name: STAT_NAME[id], short: STAT_SHORT[id], per: STAT_PER[id], value: STAT_BASE + a[id], effect: statEffectText(id, a) }));
}

/** +1/−1 한 칸(포인트·0 하한 검사). 안 되면 null */
export function statStep(a: StatAlloc, id: StatId, delta: 1 | -1, points: number): StatAlloc | null {
  const v = a[id] + delta;
  if (v < 0) return null;
  const next = { ...a, [id]: v } as StatAlloc;
  return statSum(next) <= points ? next : null;
}

/** 저장·명령 결과 코드 */
export type StatCode = 'ok' | 'duplicate' | 'bad_alloc' | 'readonly' | 'corrupt' | 'save_failed' | 'paused' | 'no_menu';
export type StatSaveMode = 'ok' | 'unavailable' | 'future' | 'corrupt';
export const STAT_TEXT: Readonly<Record<StatCode, string>> = {
  ok: '스탯을 배분했어요.',
  duplicate: '',
  bad_alloc: '가진 스탯 포인트보다 많이 나눌 수 없어요.',
  readonly: '이 기기에서는 스탯 배분을 바꿀 수 없어요.',
  corrupt: '스탯 저장이 손상돼 읽기 전용이에요(원본은 보관됨). 다른 진행은 그대로예요.',
  save_failed: '저장하지 못했어요. 다시 시도해 주세요.',
  paused: '일시정지 중에는 할 수 없어요.',
  no_menu: '마을에서 스탯 창을 열어야 배분할 수 있어요.',
};

/** 지금 영웅의 스탯 창 뷰(GameView.stats, 읽기 전용 getter) */
export interface StatLastView { readonly seq: number; readonly code: StatCode; readonly requestId: string; readonly message: string }
export interface StatView {
  readonly mode: StatSaveMode;
  readonly revision: number;
  readonly points: number;
  /** 효과에 쓰는 배분(포인트 안으로 맞춘 것) */
  readonly alloc: StatAlloc;
  readonly free: number;
  readonly last: StatLastView;
}
